import { dayOfWeek } from '@mm/shared';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api/client';
import { ExportButton } from '../../components/ExportButton';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import { Alert, Card, EmptyState, PageHeader, Spinner } from '../../components/ui/display';
import { AppIcon } from '../../components/AppIcon';
import {
  type Option,
  RemoteMultiSelect,
  SearchableSelect,
} from '../../components/ui/SearchableSelect';
import type { App, PnlByApp, PnlSeries, SettingView } from '../../types/models';
import { formatDate, formatMoney } from '../../utils/format';
import { resourceKey } from '../masters/MasterPage';
import { sources } from '../masters/sources';
import { monthLabel, OtherExpensesCard, PnlChart, PnlSummary, PnlTable, toPoint } from './pnlParts';

type Granularity = 'day' | 'month' | 'year';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Saturday or Sunday (shaded in the daily P&L). */
const isWeekend = (ymd: string) => [0, 6].includes(dayOfWeek(ymd));

const PAGES: Record<
  Granularity,
  { title: string; description: string; preset: 'thisMonth' | 'thisYear'; first: string }
> = {
  day: {
    title: 'Daily P&L',
    description:
      'Profit and loss for each day. Daily figures are estimates; month-end actuals apply to whole months.',
    preset: 'thisMonth',
    first: 'Date',
  },
  month: {
    title: 'Monthly P&L',
    description: 'Profit and loss for each month, including month-end actuals for settled months.',
    preset: 'thisYear',
    first: 'Month',
  },
  year: {
    title: 'Yearly P&L',
    description: 'Profit and loss for each calendar or financial year (April–March).',
    preset: 'thisYear',
    first: 'Year',
  },
};

export function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5 text-sm"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={clsx(
            'rounded-md px-3 py-1.5',
            value === o.value ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Daily / monthly / yearly profit & loss report. */
export function PnlReportPage({ granularity }: { granularity: Granularity }) {
  const page = PAGES[granularity];
  const [range, setRange] = useDateFilter(page.preset);
  const [params, setParams] = useSearchParams();
  // Combined (?mode=combined): one P&L for all apps (or the one app in ?appId=).
  // App-wise (default): one row per app inside every date / month / year,
  // for the apps in ?apps=id1,id2 (none = every app with entries).
  // A link for one app (?appId=) opens the combined view of that app.
  const mode: 'combined' | 'apps' =
    params.get('mode') === 'combined' || (!params.get('mode') && params.get('appId'))
      ? 'combined'
      : 'apps';
  const appId = mode === 'combined' ? (params.get('appId') ?? '') : '';
  const appIds = mode === 'apps' ? (params.get('apps') ?? '').split(',').filter(Boolean) : [];
  const [view, setView] = useState<'actual' | 'estimated'>('actual');
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingView[]>('/settings'),
    staleTime: 5 * 60_000,
  });
  const defaultYearMode =
    settings.data?.find((x) => x.key === 'reports.defaultYearMode')?.value === 'FINANCIAL'
      ? 'FINANCIAL'
      : 'CALENDAR';
  const yearMode = (params.get('yearMode') as 'CALENDAR' | 'FINANCIAL' | null) ?? defaultYearMode;

  const setParam = (key: string, value: string) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value);
    else p.delete(key);
    setParams(p, { replace: true });
  };

  const range3 = { startDate: range.startDate, endDate: range.endDate, granularity, yearMode };
  const query = useQuery({
    queryKey: ['reports', 'pnl', granularity, range.startDate, range.endDate, yearMode, appId],
    queryFn: () => api.get<PnlSeries>('/reports/pnl', { ...range3, appId: appId || undefined }),
    enabled: mode === 'combined',
    placeholderData: keepPreviousData,
  });
  const byApp = useQuery({
    queryKey: [
      'reports',
      'pnl-by-app',
      granularity,
      range.startDate,
      range.endDate,
      yearMode,
      appIds,
    ],
    queryFn: () =>
      api.get<PnlByApp>('/reports/pnl-by-app', {
        ...range3,
        appIds: appIds.join(',') || undefined,
      }),
    enabled: mode === 'apps',
    placeholderData: keepPreviousData,
  });
  // Names and logos of the apps picked for the app-wise view (also after a reload).
  const appDetails = useQueries({
    queries: appIds.map((id) => ({
      queryKey: [...resourceKey('apps'), 'detail', id],
      queryFn: () => api.get<App>(`/apps/${id}`),
      staleTime: 5 * 60_000,
    })),
  });
  const selectedOptions: Option[] = appIds.map((id, i) => ({
    value: id,
    label: appDetails[i]?.data?.name ?? '…',
    icon: appDetails[i]?.data?.iconUrl ?? null,
  }));
  const setApps = (opts: Option[]) => setParam('apps', opts.map((o) => o.value).join(','));
  const d = query.data;
  const pick = <T extends { estimated: unknown; actual: unknown }>(x: T) =>
    (view === 'actual' ? x.actual : x.estimated) as T['actual'];
  const label = (b: PnlSeries['buckets'][number]) =>
    granularity === 'day' ? formatDate(b.key).slice(0, 6) : b.label;
  const adj = d?.totals.adjustments;
  /** Daily rows compare revenue with ads spend only; other expenses are shown in their own card. */
  const rowMode = granularity === 'day' ? 'ads' : 'full';
  const dayLabel = (key: string) => (
    <span>
      {formatDate(key)}{' '}
      <span
        className={clsx(
          'text-xs',
          isWeekend(key) ? 'font-medium text-amber-700' : 'text-slate-400',
        )}
      >
        {WEEKDAYS[dayOfWeek(key)]}
      </span>
    </span>
  );
  const bucketLabel = (b: { key: string; label: string }) =>
    granularity === 'day' ? dayLabel(b.key) : b.label;
  const bucketRows = (series: PnlSeries) =>
    series.buckets.map((b) => ({
      key: b.key,
      label: bucketLabel(b),
      weekend: granularity === 'day' && isWeekend(b.key),
      f: pick(b),
      foreign: b.revenueForeign,
      adjusted: view === 'actual' && b.adjustedMonths.length > 0,
    }));
  const appName = (apps: PnlByApp['apps'], id: string) => apps.find((a) => a.id === id);
  const appCell = (apps: PnlByApp['apps'], id: string) => {
    const a = appName(apps, id);
    return (
      <span className="flex items-center gap-2">
        <AppIcon src={a?.iconUrl} name={a?.name ?? '?'} size={20} />
        {a?.name ?? 'App'}
      </span>
    );
  };
  /** One row per app inside each bucket; the bucket name only on its first row. */
  const appWiseRows = (data: PnlByApp) =>
    data.buckets.flatMap((b) =>
      b.apps.map((x, i) => ({
        key: `${b.key}-${x.appId}`,
        label: (
          <span className="flex items-center gap-3">
            <span className="w-32 shrink-0">{i === 0 ? bucketLabel(b) : ''}</span>
            {appCell(data.apps, x.appId)}
          </span>
        ),
        weekend: granularity === 'day' && isWeekend(b.key),
        f: pick(x),
        foreign: x.revenueForeign,
        groupStart: i === 0,
      })),
    );

  return (
    <>
      <PageHeader
        title={page.title}
        description={page.description}
        actions={
          <>
            {mode === 'combined' && (
              <ExportButton
                report="pnl"
                params={{
                  startDate: range.startDate,
                  endDate: range.endDate,
                  granularity,
                  yearMode,
                  appId,
                  view,
                }}
              />
            )}
            <DateRangeFilter value={range} onChange={setRange} />
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SegmentedToggle
          label="View"
          value={mode}
          onChange={(v) => {
            const p = new URLSearchParams(params);
            p.delete('appId');
            p.delete('apps');
            if (v === 'combined') p.set('mode', 'combined');
            else p.delete('mode');
            setParams(p, { replace: true });
          }}
          options={[
            { value: 'apps', label: 'App-wise' },
            { value: 'combined', label: 'Combined' },
          ]}
        />
        {mode === 'combined' ? (
          <SearchableSelect
            ariaLabel="App"
            placeholder="All apps (incl. company-level costs)"
            clearable
            className="w-72"
            value={appId}
            remote={sources.app({})}
            onChange={(v) => setParam('appId', v)}
          />
        ) : (
          <div className="w-full sm:w-96">
            <RemoteMultiSelect
              ariaLabel="Apps"
              placeholder="All apps with entries (or pick apps)"
              value={selectedOptions}
              remote={sources.app({})}
              onChange={setApps}
            />
          </div>
        )}
        <SegmentedToggle
          label="Figures"
          value={view}
          onChange={setView}
          options={[
            { value: 'actual', label: 'After month-end actuals' },
            { value: 'estimated', label: 'Estimated (daily entries)' },
          ]}
        />
        {granularity === 'year' && (
          <SegmentedToggle
            label="Year"
            value={yearMode}
            onChange={(v) => setParam('yearMode', v)}
            options={[
              { value: 'CALENDAR', label: 'Calendar year' },
              { value: 'FINANCIAL', label: 'Financial year' },
            ]}
          />
        )}
      </div>

      {mode === 'apps' && byApp.isError && (
        <Alert tone="error">
          {byApp.error instanceof ApiError ? byApp.error.message : 'Failed to load'}
        </Alert>
      )}
      {mode === 'apps' && byApp.isLoading && <Spinner />}
      {mode === 'apps' && byApp.data && (
        <>
          <Card
            title="App-wise totals"
            description="Each app for the whole period. Company-level expenses are not split to apps."
            className="mb-6"
            bodyClassName="p-0"
          >
            {byApp.data.apps.length === 0 ? (
              <EmptyState title="No app has entries in this period" />
            ) : (
              <PnlTable
                mode="full"
                firstHeader="App"
                rows={byApp.data.totals.map((t) => ({
                  key: t.appId,
                  label: appCell(byApp.data.apps, t.appId),
                  f: pick(t),
                  foreign: t.revenueForeign,
                }))}
              />
            )}
          </Card>
          {byApp.data.apps.length > 0 && (
            <Card bodyClassName="p-0">
              <PnlTable mode={rowMode} firstHeader={page.first} rows={appWiseRows(byApp.data)} />
            </Card>
          )}
          <p className="mt-2 text-xs text-slate-500">
            {mode === 'apps' &&
              appIds.length === 0 &&
              'Days without entries for an app are left out. '}
            Ads profit = revenue − ads spend; net profit = ads profit − the app&apos;s own other
            expenses; all before GST.
            {view === 'actual' &&
              granularity !== 'day' &&
              ' Settled months include month-end actuals.'}
          </p>
        </>
      )}

      {mode === 'combined' && query.isError && (
        <Alert tone="error">
          {query.error instanceof ApiError ? query.error.message : 'Failed to load'}
        </Alert>
      )}
      {mode === 'combined' && query.isLoading && <Spinner />}

      {mode === 'combined' && d && (
        <>
          <PnlSummary
            f={pick(d.totals)}
            revenueForeign={d.totals.revenueForeign}
            adjustmentNote={
              <>
                {appId && 'Company-level expenses are excluded when one app is selected. '}
                {view === 'actual' && adj && adj.months.length > 0 && (
                  <>
                    Includes month-end actuals for {adj.months.map(monthLabel).join(', ')} (profit{' '}
                    {Number(adj.operatingProfit) >= 0 ? '+' : ''}
                    {formatMoney(adj.operatingProfit)}).{' '}
                  </>
                )}
                {view === 'actual' &&
                  adj &&
                  adj.partialMonths.length > 0 &&
                  `Actuals for ${adj.partialMonths.map(monthLabel).join(', ')} are not applied: the range covers only part of the month. `}
                {view === 'actual' &&
                  granularity === 'day' &&
                  adj &&
                  adj.months.length > 0 &&
                  'Daily rows show estimates; the totals include the actuals.'}
              </>
            }
          />

          <Card title="Revenue, expenses and profit" className="mb-6">
            {d.buckets.every(
              (b) =>
                Number(b.estimated.revenue) === 0 && Number(b.estimated.operatingExpense) === 0,
            ) ? (
              <EmptyState title="No data in this period" />
            ) : (
              <PnlChart
                mode={rowMode}
                data={d.buckets.map((b) => toPoint(label(b), pick(b), rowMode))}
              />
            )}
          </Card>

          <Card bodyClassName="p-0">
            <PnlTable
              mode={rowMode}
              firstHeader={page.first}
              rows={bucketRows(d)}
              total={pick(d.totals)}
              totalForeign={d.totals.revenueForeign}
            />
          </Card>
          <div className="mt-6">
            <OtherExpensesCard data={d.otherExpenses} startDate={d.startDate} endDate={d.endDate} />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {granularity === 'day' && (
              <>
                <span className="mr-1 inline-block size-2.5 rounded-sm bg-amber-100 align-middle ring-1 ring-amber-200" />
                Weekend (Saturday and Sunday).{' '}
              </>
            )}
            Ads profit = revenue − ads spend; net profit = ads profit − other expenses; all before
            GST. GST is tracked separately; cash outflow includes it.
          </p>
        </>
      )}
    </>
  );
}
